import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { SimpleCollectionPreview } from '@sorare/core/src/components/collections/SimpleCollectionPreview';
import { Dialog } from '@sorare/core/src/components/dialog';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { getDeliverableItemImageUrl } from '@sorare/core/src/lib/deliverableItem';

import { TextSwitchRarity } from '#src/atoms/TextSwitchRarity/index.tsx';

import type {
  EligibleCollectionsDialog_cardCollection,
  EligibleCollectionsDialog_deliverableItemVariantsStocks,
} from './__generated__/index.graphql.ts';

const Content = styled(Vertical).attrs({ gap: 1, center: true })`
  padding: var(--triple-unit);
`;

const Title = styled(LabelL)`
  padding: 0 var(--unit);
`;

const FullWidth = styled.div`
  width: 100%;
`;

const Img = styled.img`
  width: var(--double-unit);
  height: var(--double-unit);
  object-fit: contain;
  object-position: center;
`;

type DeliverableItem =
  EligibleCollectionsDialog_deliverableItemVariantsStocks['deliverableItem'];

type Props = {
  promotionName: string;
  collections?: Nullable<EligibleCollectionsDialog_cardCollection[]>;
  stocks?: Nullable<EligibleCollectionsDialog_deliverableItemVariantsStocks[]>;
  needsStockInfos: boolean;
  open: boolean;
  onClose: () => void;
};

const orderedRarities: Rarity[] = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
];

export const EligibleCollectionsDialog = ({
  promotionName,
  collections,
  stocks,
  needsStockInfos,
  open,
  onClose,
}: Props) => {
  const [selectedRarity, setSelectedRarity] = useState<Rarity>(Rarity.limited);
  if (!collections?.length) return null;

  const collectionsByRarity = groupBy(
    collections,
    c => c.rarity || Rarity.limited
  );

  const rarityCollections = collectionsByRarity[selectedRarity];
  const raritiesOptions = orderedRarities.filter(
    r => (collectionsByRarity[r]?.length || 0) > 0
  );

  const deliverableItemWithStockByTeamSlug = (stocks || []).reduce<{
    [teamSlug: string]: {
      deliverableItem: DeliverableItem;
      outOfStock: boolean;
    }[];
  }>((acc, stock) => {
    stock.teamVariants?.filter(Boolean).forEach(teamVariant => {
      const teamSlug = teamVariant.team.slug;
      if (!acc[teamSlug]) {
        acc[teamSlug] = [];
      }

      acc[teamSlug].push({
        deliverableItem: stock.deliverableItem,
        outOfStock: teamVariant.outOfStock,
      });
    });
    return acc;
  }, {});

  const additionalInfos = (teamSlug?: string | null) => {
    if (!needsStockInfos) return null;
    if (!teamSlug) return null;
    const teamStocks = deliverableItemWithStockByTeamSlug[teamSlug];
    if (!teamStocks || teamStocks.length === 0) return null;
    return (
      <Vertical>
        {teamStocks.map(s => (
          <Chip
            key={s.deliverableItem.slug}
            color={s.outOfStock ? 'red' : 'green'}
            size="compact"
          >
            <Img
              src={getDeliverableItemImageUrl(s.deliverableItem)}
              alt={s.deliverableItem.name}
            />
            {s.outOfStock ? (
              <FormattedMessage
                id="CartPromo.EligibleCollectionsDialog.outOfStock"
                defaultMessage="Out of stock"
              />
            ) : (
              <FormattedMessage
                id="CartPromo.EligibleCollectionsDialog.inStock"
                defaultMessage="In stock"
              />
            )}
          </Chip>
        ))}
      </Vertical>
    );
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={onClose}
      title={
        <Title bold>
          <FormattedMessage
            id="CartPromo.EligibleCollectionsDialog.title"
            defaultMessage="Eligible Collections for {promotionName}"
            values={{
              promotionName,
            }}
          />
        </Title>
      }
    >
      <Content>
        <TextSwitchRarity
          rarities={raritiesOptions}
          selectedRarity={selectedRarity}
          onChange={setSelectedRarity}
        />

        {rarityCollections?.map(c => (
          <FullWidth key={c.slug}>
            <SimpleCollectionPreview
              cardCollection={c}
              additionalInfos={additionalInfos(c.team?.slug)}
            />
          </FullWidth>
        ))}
      </Content>
    </Dialog>
  );
};

EligibleCollectionsDialog.fragments = {
  cardCollection: gql`
    fragment EligibleCollectionsDialog_cardCollection on CardCollection {
      slug
      name
      rarity
      ...SimpleCollectionPreview_cardCollection
    }
    ${SimpleCollectionPreview.fragments.cardCollection}
  ` as TypedDocumentNode<EligibleCollectionsDialog_cardCollection>,
  deliverableItemVariantsStocks: gql`
    fragment EligibleCollectionsDialog_deliverableItemVariantsStocks on DeliverableItemVariantsStocks {
      id
      deliverableItem {
        slug
        name
        ...getDeliverableItemImageUrl_deliverableItem
      }
      teamVariants {
        id
        outOfStock
        team {
          ... on Club {
            slug
          }
          ... on NationalTeam {
            slug
          }
        }
      }
    }
    ${getDeliverableItemImageUrl.fragments.deliverableItem}
  ` as TypedDocumentNode<EligibleCollectionsDialog_deliverableItemVariantsStocks>,
};
